import { apiClient } from '@/utils/api-client';

export interface LogoUploadResponse {
  logo_url: string;
  message: string;
}

export const logoUploadService = {
  /**
   * Upload a logo file to S3 via the backend API
   * @param accountId - The account ID
   * @param file - The file to upload
   * @returns Promise with the uploaded logo URL
   */
  async uploadLogo(accountId: string, file: File): Promise<LogoUploadResponse> {
    // Validate file type on frontend
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or SVG image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 5MB.');
    }

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('logo', file);

    // Send to backend - uses httpOnly cookie for auth
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/accounts/${accountId}/upload_logo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload logo');
    }

    const data = await response.json();
    return data.data.attributes;
  },

  /**
   * Delete the current logo
   * @param accountId - The account ID
   * @returns Promise indicating success
   */
  async deleteLogo(accountId: string): Promise<void> {
    // apiClient.delete throws on error, so no need to check response
    await apiClient.delete(`/api/accounts/${accountId}/delete_logo`);
  },

  /**
   * Generate a preview URL for a file
   * @param file - The file to preview
   * @returns Promise with the preview URL
   */
  async generatePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },
};